/**
 * Responsibility: say how old the prices on screen are.
 *
 * **Shown always, not only when stale.** A badge that appears only on old
 * data is a badge nobody learns to read, and its absence is then ambiguous -
 * fresh, or broken, or a version that does not have the feature. Saying
 * "read 2 hours ago" every time makes the one that says "41 days" legible.
 *
 * Unknown is its own state. Rows crawled before the timestamp existed carry
 * no date, and calling that fresh would be the single mistake this is here
 * to prevent.
 */
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import ScheduleIcon from '@mui/icons-material/ScheduleOutlined';
import { STALE_AFTER_DAYS } from './priceAge';

function phrase(days: number): string {
  const hours = days * 24;
  if (hours < 1) return 'read just now';
  if (hours < 24) return `read ${Math.round(hours)}h ago`;
  return `read ${Math.round(days)}d ago`;
}

export function PriceAgeBadge({ days, live = false }: { days: number | null; live?: boolean }) {
  if (live) {
    return (
      <Tooltip title="These prices were read from the card's own page just now.">
        <Chip size="small" color="success" variant="outlined" label="live prices" />
      </Tooltip>
    );
  }

  if (days === null) {
    return (
      <Tooltip title="These prices predate the crawl recording when it read them. The next pass will date them.">
        <Chip size="small" variant="outlined" color="warning" label="age unknown" />
      </Tooltip>
    );
  }

  const stale = days > STALE_AFTER_DAYS;
  return (
    <Tooltip
      title={
        stale
          ? 'The crawler has not reached this set lately. Opening the card reads its page directly.'
          : 'From the catalogue, refreshed by the crawler.'
      }
    >
      <Chip
        size="small"
        icon={<ScheduleIcon />}
        variant="outlined"
        color={stale ? 'warning' : 'default'}
        label={phrase(days)}
      />
    </Tooltip>
  );
}
